import { Building2, ChevronsUpDown, LogOut, Plus, Settings, User } from 'lucide-react'
import { Link, router, usePage } from '@inertiajs/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { DashboardPageProps } from '@/types'

export function AccountSwitcher() {
  const { auth, sidebar } = usePage<DashboardPageProps>().props
  const currentAccount = auth.account
  const accounts = sidebar?.accounts || []
  const permissions = sidebar?.permissions || {}
  const accountSlug = currentAccount?.slug
  const user = auth.user
  const identity = auth.identity

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label={`Open account menu for ${currentAccount?.name || 'your account'}`}
          className="h-11 max-w-44 gap-1.5 rounded-lg px-2"
        >
          <span className="min-w-0 truncate text-sm">
            {currentAccount?.name || 'Select space'}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-72 rounded-lg"
        align="end"
        side="bottom"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="grid min-w-0 px-2 py-2 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{user?.name || 'User'}</span>
            <span className="truncate text-xs text-muted-foreground">
              {identity?.email_address}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Your spaces
        </DropdownMenuLabel>
        {accounts.map((account) => (
          <DropdownMenuItem
            key={account.id}
            asChild
            className="gap-2 p-2"
          >
            <Link href={`/${account.slug}/businesses`}>
              <div className="flex size-6 items-center justify-center rounded-sm border">
                {account.type === 'TeamAccount' ? (
                  <Building2 className="size-4 shrink-0" />
                ) : (
                  <User className="size-4 shrink-0" />
                )}
              </div>
              <span className="truncate">{account.name}</span>
              {account.id === currentAccount?.id && (
                <span className="ml-auto text-xs text-muted-foreground">Current</span>
              )}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="gap-2 p-2">
          <Link href="/registration/complete">
            <div className="flex size-6 items-center justify-center rounded-md border bg-background">
              <Plus className="size-4" />
            </div>
            <span className="font-medium text-muted-foreground">Create a space</span>
          </Link>
        </DropdownMenuItem>
        {permissions.can_view_settings && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="gap-2 p-2">
              <Link href={`/${accountSlug}/settings`}>
                <Settings className="size-4 shrink-0 text-muted-foreground" />
                <span>Settings</span>
              </Link>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem asChild className="gap-2 p-2">
          <Link href={`/${accountSlug}/profile/edit`}>
            <User className="size-4 shrink-0 text-muted-foreground" />
            <span>Profile</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2 p-2" onClick={() => router.delete('/session')}>
          <LogOut className="size-4 shrink-0 text-muted-foreground" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
